

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  content: string;
}

export const MarkdownRenderer: React.FC<Props> = ({ content }) => {
  // Filter out system data blocks and clean up artifacts
  const cleanContent = React.useMemo(() => {
    if (!content) return "";
    
    let cleaned = content;

    // 1. Remove System Data Blocks
    cleaned = cleaned.replace(/\[\[SYSTEM_DATA_START\]\][\s\S]*?\[\[SYSTEM_DATA_END\]\]/g, '');
    cleaned = cleaned.replace(/^\[[a-zA-Z0-9_-]+\]:\s*.*$/gm, '');

    // 2. Remove specific artifacts requested by user (<br>, **)
    // CRITICAL FIX: Replace <br> with SPACE (' '), not newline ('\n').
    // Markdown tables require rows to be on a single line. Inserting \n breaks the table structure.
    cleaned = cleaned.replace(/<br\s*\/?>/gi, ' '); 
    
    // Remove Markdown bold markers (**) but keep the text inside.
    cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '$1');

    // 3. Clean up empty code blocks
    cleaned = cleaned.replace(/```[\s\n\r]*```/g, '');
    
    // 4. Remove "Reporter" signature lines
    cleaned = cleaned.replace(/^(汇报人|签字|Reporter|Signature)[:：].*$/gm, '');
    
    return cleaned.trim();
  }, [content]);

  return (
    // Reduced padding from p-4 to p-3 to allow more characters per line on mobile
    <div className="markdown-body p-3 md:p-6 bg-white rounded-xl shadow-sm border border-gray-100 leading-relaxed text-gray-800">
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
            table: ({node, ...props}) => (
                <div className="overflow-x-auto my-3 border border-gray-200 rounded-lg">
                    {/* table-auto allows the browser to size columns based on content length */}
                    <table {...props} className="w-full divide-y divide-gray-200 table-auto" />
                </div>
            ),
            thead: ({node, ...props}) => (
                <thead {...props} className="bg-gray-50" />
            ),
            th: ({node, ...props}) => (
                <th {...props} className="px-3 py-2 text-left text-xs font-bold text-gray-600 uppercase tracking-wider bg-slate-50 border-b border-gray-200 whitespace-nowrap" />
            ),
            td: ({node, ...props}) => (
                <td {...props} className="px-3 py-2 text-sm text-gray-700 border-b border-gray-100 align-top" />
            ),
            img: ({node, ...props}) => (
                <img {...props} className="max-w-full h-auto rounded-lg shadow-sm my-2" loading="lazy" />
            )
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  );
};
